/**
 * Kiian free-gift manager (single-gift edition).
 *
 * Auto-adds the gift when the gift-excluded subtotal reaches the
 * threshold, and removes it when the cart drops below. The gift is
 * non-removable (Jenny Bird model) — it always accompanies a qualifying
 * cart, so there is no opt-out/restore flow.
 *
 * Every decision is made against the server cart, via the pure functions in
 * assets/gift-logic.js (loaded as a preceding classic <script> — see
 * snippets/header-actions.liquid — so `KiianGiftLogic` is a global by the
 * time this file runs). Eligibility/normalization logic lives there, not
 * here, so it can be unit tested with plain Node
 * (shopify/tests/gift-manager.test.mjs). This file is the DOM/network glue:
 * fetching the cart, applying the plan `KiianGiftLogic.decideGiftPlan`
 * returns, and rendering the progress rail / staging preview states.
 *
 * Note: a cart may still carry a stale `gift_opt_out` attribute from earlier
 * testing/versions of the opt-out feature. It's inert — nothing in this file
 * reads it — and is left alone rather than reintroducing code to clear it.
 */

const DEBOUNCE_MS = 800;
const MIN_CHECK_INTERVAL_MS = 3000;
const REQUEST_TIMEOUT_MS = 10000;
const MAX_MUTATIONS_PER_WINDOW = 4;
const MUTATION_WINDOW_MS = 10000;
const STAND_DOWN_KEY = 'kc_gift_offer_inactive';
const STAND_DOWN_MS = 6 * 60 * 60 * 1000;
const CART_UPDATE_EVENT = 'cart:update';
const PREVIEW_STATES = new Set(['progress', 'unlocked', 'soldout']);
const GIFT_ROW_SELECTOR = '[data-kc-gift-line="true"]';
const GIFT_PILL_SELECTOR = '.kc-gift-pill';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
// How long to wait for the row to first appear in the DOM (rare — see the
// doc comment on #triggerGiftRowEntrance for why it's normally there
// already by the time this runs).
const GIFT_ROW_APPEAR_TIMEOUT_MS = 3000;
// How long to keep watching for the drawer's own follow-up re-render
// swapping out the row's DOM node mid-animation (measured live: lands
// ~100-400ms after the add; 1200ms gives comfortable margin).
const GIFT_ROW_REPLACEMENT_WATCH_MS = 1200;
const GIFT_ROW_MAX_REAPPLIES = 3;
// Kiian review 2026-09-28: 420ms/520ms+120ms on a weak ease-in-out started slow and
// landed the FREE pill ~0.6s late; tightened to UI timing on a strong ease-out.
const GIFT_ROW_ANIMATION_MS = 260;
const GIFT_ENTRANCE_EASING = 'cubic-bezier(0.23, 1, 0.32, 1)';
const GIFT_ROW_REDUCED_ANIMATION_MS = 200;
const GIFT_PILL_ANIMATION_MS = 300;
const GIFT_PILL_ANIMATION_DELAY_MS = 60;

class GiftManager {
  #isUpdating = false;
  #recheckQueued = false;
  #debounceTimer = null;
  #lastCheckAt = 0;
  #pendingCart = null;
  #mutationTimestamps = [];
  #disabled = false;
  #giftEntranceObserver = null;

  constructor() {
    document.addEventListener(CART_UPDATE_EVENT, (event) => {
      if (this.#isPreviewMode) {
        setTimeout(() => this.#renderPreview(), 50);
        return;
      }

      const source = event.detail?.data?.source;
      const resource = event.detail?.resource;
      const cart =
        Array.isArray(resource?.items) && Number.isFinite(resource?.item_count) ? resource : null;
      // Kiian 2026-09-28: when this update crosses the gift threshold either
      // way, add or remove the gift now instead of after the 800ms-3s settle
      // wait; the drawer showed "FREE Gift Unlocked!" with no gift for ~1.5s,
      // and Checkout was clickable before a lapsed gift left. The mutation
      // tripwire still guards against add/remove loops.
      let crossesGiftLine = false;
      if (cart && this.#config?.variantId && window.KiianGiftLogic?.decideGiftPlan) {
        try {
          const plan = window.KiianGiftLogic.decideGiftPlan({
            items: cart.items,
            config: this.#config,
            giftOfferInactive: this.#hasActiveStandDown(),
          });
          crossesGiftLine = plan.actions.length > 0;
        } catch (error) {
          crossesGiftLine = false;
        }
      }
      this.#schedule(source === 'gift-manager' || crossesGiftLine, cart);
    });

    const cartDialog = document.querySelector('.cart-drawer__dialog');
    cartDialog?.addEventListener('toggle', () => {
      if (this.#isPreviewMode && cartDialog.open) {
        setTimeout(() => this.#renderPreview(), 0);
      }
    });

    if (this.#isPreviewMode) {
      this.#renderPreview();
      if (
        this.#config?.previewOnly &&
        new URLSearchParams(window.location.search).get('gift_preview') === '1'
      ) {
        setTimeout(() => {
          document.querySelector('[data-testid="cart-drawer-trigger"]')?.click();
          setTimeout(() => this.#renderPreview(), 350);
        }, 200);
      }
      return;
    }

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.#schedule(true);
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.#schedule(true);
    });

    this.#schedule(true);
  }

  get #config() {
    const element = document.querySelector('[data-kc-gift-config]');
    if (!element) return null;

    try {
      return JSON.parse(element.textContent);
    } catch {
      return null;
    }
  }

  get #previewState() {
    const state = new URLSearchParams(window.location.search).get('gift_state');
    return PREVIEW_STATES.has(state) ? state : null;
  }

  get #isPreviewMode() {
    // Demo params (?gift_state=…, ?gift_preview=1) and the previewOnly config
    // flag must be inert on the published theme — only an unpublished/staging
    // theme (role !== 'main') may ever enter preview mode.
    const themeRole = window.Shopify?.theme?.role;
    if (!themeRole || themeRole === 'main') return false;
    return Boolean(this.#previewState || this.#config?.previewOnly);
  }

  #schedule(immediate = false, cart = null) {
    if (this.#disabled) return;
    if (cart) this.#pendingCart = cart;
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => this.#check(immediate), immediate ? 50 : DEBOUNCE_MS);
  }

  async #check(forced = false) {
    if (this.#disabled || this.#isPreviewMode) return;

    if (this.#isUpdating) {
      this.#recheckQueued = true;
      // A cart snapshot taken while an add was in flight may predate the gift;
      // drop it so the recheck fetches the real cart (no second gift).
      this.#pendingCart = null;
      return;
    }

    const now = Date.now();
    if (!forced && now - this.#lastCheckAt < MIN_CHECK_INTERVAL_MS) {
      clearTimeout(this.#debounceTimer);
      this.#debounceTimer = setTimeout(
        () => this.#check(),
        MIN_CHECK_INTERVAL_MS - (now - this.#lastCheckAt)
      );
      return;
    }
    this.#lastCheckAt = now;

    const config = this.#config;
    if (!config?.variantId) return;

    const giftOfferInactive = this.#hasActiveStandDown();

    this.#isUpdating = true;
    try {
      const pendingCart = this.#pendingCart;
      this.#pendingCart = null;
      const cart = pendingCart || (await this.#fetchCart());
      if (!cart) return;

      // Gift identity is variant id ONLY (R7) — the `_gift` line-item
      // property is presentation-only. Eligibility + line normalization are
      // pure functions in assets/gift-logic.js so they're unit-testable
      // outside the browser; see shopify/tests/gift-manager.test.mjs.
      const plan = window.KiianGiftLogic.decideGiftPlan({
        items: cart.items,
        config,
        giftOfferInactive,
      });

      this.#syncRuntimeRail(plan.giftUnavailable, plan.offerSubtotal, config);

      if (plan.standDown === 'clear') this.#clearStandDown();
      if (plan.standDown === 'set') {
        this.#setStandDown();
        this.#setRuntimeUnavailable();
      }

      // Apply one action, then let #afterMutation's recheck re-derive a
      // fresh plan against the post-mutation cart. decideGiftPlan is a pure
      // function of the current snapshot, so this converges to "actions: []"
      // in a bounded number of ticks (guarded by the mutation tripwire)
      // without this method needing to track any incremental state itself.
      const action = plan.actions[0];
      if (!action) return;

      if (action.type === 'add') {
        await this.#addGift(config.variantId, config);
        this.#triggerGiftRowEntrance();
      } else if (action.type === 'setQuantity') {
        if (action.quantity === 0) this.#collapseRow(action.key);
        await this.#changeLine(action.key, action.quantity, config);
      }
      this.#afterMutation();
    } catch (error) {
      console.error('[gift-manager]', error);
    } finally {
      this.#isUpdating = false;
      if (this.#recheckQueued) {
        this.#recheckQueued = false;
        this.#schedule(true);
      }
    }
  }

  async #fetchCart() {
    const response = await this.#fetchWithTimeout(`${Theme.routes.cart_url}.js`, {
      cache: 'no-store',
    });
    if (!response.ok) return null;
    return response.json();
  }

  #tripwire() {
    const now = Date.now();
    this.#mutationTimestamps = this.#mutationTimestamps.filter(
      (timestamp) => now - timestamp < MUTATION_WINDOW_MS
    );
    this.#mutationTimestamps.push(now);

    if (this.#mutationTimestamps.length > MAX_MUTATIONS_PER_WINDOW) {
      this.#disabled = true;
      console.warn('[gift-manager] mutation tripwire hit; disabled for this page view');
      return true;
    }

    return false;
  }

  async #addGift(variantId, config) {
    if (this.#tripwire()) return;

    // `_gift: 'true'` is presentation-only (it never drives identity or
    // eligibility, see assets/gift-logic.js) — kept so this manager's own
    // additions are easy to spot in the network tab. Whether the add
    // actually landed at $0 is verified by the NEXT #check() tick's
    // decideGiftPlan call (its `freeLine` search), not here — that keeps
    // this method a plain POST-and-dispatch with no discount-allocation
    // assumptions baked in (KTD7).
    const body = JSON.stringify({
      items: [{ id: variantId, quantity: 1, properties: { _gift: 'true' } }],
      sections: config.sectionId,
      sections_url: window.location.pathname,
    });
    const data = await this.#request(Theme.routes.cart_add_url, body);
    if (!data) return;

    this.#dispatch(data);
  }

  /**
   * Plays a one-shot entrance animation on the just-added gift row, driven
   * by the Web Animations API rather than a CSS class.
   *
   * Called ONLY from the `action.type === 'add'` branch of #check() — never
   * on remove/setQuantity, and never when the gift was already present on
   * load (decideGiftPlan returns no actions in that case, so #check never
   * reaches here) — so this always runs exactly once per real add.
   *
   * Why WAAPI and not a CSS class: measured live, gift-manager's own
   * post-add dispatch triggers a SECOND async section re-render that lands
   * ~100-400ms after the add and replaces the row's DOM node — taking any
   * CSS class (and an @starting-style entrance, tried first) down with it.
   * A WAAPI Animation isn't tied to a class staying put; #playGiftRowEntrance
   * below detects the node swap and re-targets the SAME in-flight animation
   * (continuing from its currentTime) onto the replacement node instead of
   * restarting or silently dying.
   *
   * Drawer-visibility guard: if the cart-drawer feature exists on this
   * theme (`.cart-drawer__dialog` present) and it is currently closed, the
   * row is being morphed into hidden/background DOM — skip the entrance
   * entirely, so opening the drawer later shows the final state with no
   * animation (a WAAPI animation on a display:none subtree simply doesn't
   * progress until it's shown, which would otherwise make it play the
   * moment the dialog opens — the opposite of what's wanted). No dialog on
   * the page (drawer disabled, or the standalone /cart page, which never
   * renders the drawer markup) means the row is always visible to the
   * shopper, so the entrance always plays there.
   */
  #triggerGiftRowEntrance() {
    const dialog = document.querySelector('.cart-drawer__dialog');
    if (dialog && !dialog.open) return;

    this.#giftEntranceObserver?.disconnect();
    this.#giftEntranceObserver = null;

    const row = this.#findGiftRow();
    if (row) {
      this.#playGiftRowEntrance(row);
      return;
    }

    // Row not in the DOM yet — bounded wait for it to first appear.
    const root = this.#giftAnimationRoot();
    const appearObserver = new MutationObserver(() => {
      const foundRow = this.#findGiftRow();
      if (!foundRow) return;

      appearObserver.disconnect();
      if (this.#giftEntranceObserver === appearObserver) this.#giftEntranceObserver = null;
      this.#playGiftRowEntrance(foundRow);
    });
    appearObserver.observe(root, { childList: true, subtree: true });
    this.#giftEntranceObserver = appearObserver;

    setTimeout(() => {
      appearObserver.disconnect();
      if (this.#giftEntranceObserver === appearObserver) this.#giftEntranceObserver = null;
    }, GIFT_ROW_APPEAR_TIMEOUT_MS);
  }

  /**
   * The element to scope gift-row lookups and the replacement-watching
   * MutationObserver to: the drawer's custom element when the cart-drawer
   * feature is on this theme, otherwise the whole document (covers the
   * standalone /cart page, which never renders drawer markup at all).
   * @returns {Element | Document}
   */
  #giftAnimationRoot() {
    return document.querySelector('cart-drawer-component') || document;
  }

  /**
   * @returns {HTMLElement | null} The current gift row, if any.
   */
  #findGiftRow() {
    return this.#giftAnimationRoot().querySelector(GIFT_ROW_SELECTOR);
  }

  /**
   * Starts the entrance animation on `row` (and its FREE pill, if present),
   * then watches for up to GIFT_ROW_REPLACEMENT_WATCH_MS for the row's DOM
   * node being swapped out from under it by a later re-render — re-applying
   * the same animation (carrying over currentTime, so it continues rather
   * than restarts) onto the replacement node when that happens, up to
   * GIFT_ROW_MAX_REAPPLIES times.
   * @param {HTMLElement} row
   */
  #playGiftRowEntrance(row) {
    const reduced = Boolean(window.matchMedia?.(REDUCED_MOTION_QUERY).matches);
    const easing = GIFT_ENTRANCE_EASING;

    const rowKeyframes = reduced
      ? [{ opacity: 0 }, { opacity: 1 }]
      : [
          { opacity: 0, transform: 'translateY(-0.6rem)' },
          { opacity: 1, transform: 'none' },
        ];
    const rowOptions = reduced
      ? { duration: GIFT_ROW_REDUCED_ANIMATION_MS, easing: 'linear', fill: 'both' }
      : { duration: GIFT_ROW_ANIMATION_MS, easing, fill: 'both' };

    const pillKeyframes = reduced
      ? [{ opacity: 0 }, { opacity: 1 }]
      : [
          { opacity: 0, transform: 'scale(0.85)' },
          { opacity: 1, transform: 'scale(1.04)', offset: 0.7 },
          { opacity: 1, transform: 'scale(1)' },
        ];
    const pillOptions = reduced
      ? { duration: GIFT_ROW_REDUCED_ANIMATION_MS, easing: 'linear', fill: 'both' }
      : { duration: GIFT_PILL_ANIMATION_MS, delay: GIFT_PILL_ANIMATION_DELAY_MS, easing, fill: 'both' };

    /** @type {{ rowNode: HTMLElement, rowAnim: Animation | null, pillAnim: Animation | null, reapplies: number }} */
    const state = { rowNode: row, rowAnim: null, pillAnim: null, reapplies: 0 };

    // Strips the fill:'both' override once an animation's own timeline
    // finishes naturally, so it doesn't linger and mask the row's normal
    // styled end state. Rejects (silently, via .catch) when the animation
    // is instead cancelled/replaced mid-flight — expected, not an error.
    const settleWhenFinished = (anim, isStillCurrent) => {
      if (!anim) return;
      anim.finished
        .then(() => {
          if (isStillCurrent()) anim.cancel();
        })
        .catch(() => {});
    };

    const applyTo = (targetRow) => {
      const rowAnim = targetRow.animate(rowKeyframes, rowOptions);
      const pillNode = targetRow.querySelector(GIFT_PILL_SELECTOR);
      const pillAnim = pillNode ? pillNode.animate(pillKeyframes, pillOptions) : null;

      state.rowNode = targetRow;
      state.rowAnim = rowAnim;
      state.pillAnim = pillAnim;

      settleWhenFinished(rowAnim, () => state.rowAnim === rowAnim);
      settleWhenFinished(pillAnim, () => state.pillAnim === pillAnim);

      // Belt-and-braces: some engines cancel a running animation when its
      // target is detached from the document. Treat that exactly like a
      // detected node replacement so the row still gets its entrance.
      rowAnim.oncancel = () => reapplyIfReplaced();
      if (pillAnim) pillAnim.oncancel = () => reapplyIfReplaced();
    };

    const reapplyIfReplaced = () => {
      const currentRow = this.#findGiftRow();
      if (!currentRow || currentRow === state.rowNode) return;
      if (state.reapplies >= GIFT_ROW_MAX_REAPPLIES) return;

      const carriedRowTime = state.rowAnim?.currentTime ?? 0;
      const carriedPillTime = state.pillAnim ? state.pillAnim.currentTime : null;

      state.reapplies += 1;
      applyTo(currentRow);

      // Continue seamlessly from where the previous node's animation was,
      // rather than restarting from 0 on the new node.
      if (state.rowAnim) state.rowAnim.currentTime = carriedRowTime;
      if (state.pillAnim && carriedPillTime !== null) state.pillAnim.currentTime = carriedPillTime;
    };

    applyTo(row);

    // Kiian 2026-09-28: the gift row is inserted above the shopper's items, which
    // made them jump down a row height in one frame. Slide them down instead,
    // same timing as the gift's own entrance (transform only; skipped when
    // reduced motion is on, where the jump is the gentler option).
    if (!reduced) {
      // Layout distance (transforms excluded) from the gift row to the line that
      // now follows it = how far that line, and every line after it, moved.
      const next = row.nextElementSibling;
      const shift = next instanceof HTMLElement ? next.offsetTop - row.offsetTop : 0;
      for (let sibling = shift > 0 ? next : null; sibling; sibling = sibling.nextElementSibling) {
        sibling.animate([{ transform: `translateY(${-shift}px)` }, { transform: 'none' }], {
          duration: GIFT_ROW_ANIMATION_MS,
          easing: GIFT_ENTRANCE_EASING,
        });
      }
    }

    const replaceObserver = new MutationObserver(reapplyIfReplaced);
    replaceObserver.observe(this.#giftAnimationRoot(), { childList: true, subtree: true });
    this.#giftEntranceObserver = replaceObserver;

    setTimeout(() => {
      replaceObserver.disconnect();
      if (this.#giftEntranceObserver === replaceObserver) this.#giftEntranceObserver = null;
    }, GIFT_ROW_REPLACEMENT_WATCH_MS);
  }

  #hasActiveStandDown() {
    try {
      const timestamp = Number.parseInt(sessionStorage.getItem(STAND_DOWN_KEY) || '', 10);
      if (Number.isFinite(timestamp) && Date.now() - timestamp < STAND_DOWN_MS) return true;
      sessionStorage.removeItem(STAND_DOWN_KEY);
    } catch {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
    return false;
  }

  #setStandDown() {
    try {
      sessionStorage.setItem(STAND_DOWN_KEY, String(Date.now()));
    } catch {
      // The current pass still fails closed even when storage is unavailable.
    }
  }

  #clearStandDown() {
    try {
      sessionStorage.removeItem(STAND_DOWN_KEY);
    } catch {
      // Nothing else is required when storage is unavailable.
    }
  }

  /**
   * Kiian 2026-09-28: fold the gift row away (the theme's own line-removal
   * collapse) while the removal request is in flight, so the rows below slide
   * up instead of jumping when the re-render drops it.
   */
  #collapseRow(lineKey) {
    const row = document.getElementById(`cart-line-${String(lineKey).replaceAll(':', '-')}`);
    if (!(row instanceof HTMLElement) || row.classList.contains('removing')) return;
    row.style.setProperty('--row-height', `${row.clientHeight}px`);
    row.classList.add('removing');
  }

  async #changeLine(lineKey, quantity, config) {
    if (this.#tripwire()) return;

    const body = JSON.stringify({
      id: lineKey,
      quantity,
      sections: config.sectionId,
      sections_url: window.location.pathname,
    });
    const data = await this.#request(Theme.routes.cart_change_url, body);
    if (!data) return;

    this.#dispatch(data);
  }

  async #request(url, body) {
    const response = await this.#fetchWithTimeout(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Cart update failed');
    return data;
  }

  async #fetchWithTimeout(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } finally {
      clearTimeout(timeout);
    }
  }

  #dispatch(data) {
    const config = this.#config;
    const sectionHtml = data.sections?.[config?.sectionId];
    const sectionDocument = sectionHtml
      ? new DOMParser().parseFromString(sectionHtml, 'text/html')
      : null;
    const renderedCount = Number.parseInt(
      sectionDocument?.querySelector('[ref="cartItemCount"]')?.textContent || '',
      10
    );
    const itemCount = Number.isFinite(data.item_count) ? data.item_count : renderedCount;

    document.dispatchEvent(new CustomEvent(CART_UPDATE_EVENT, {
      bubbles: true,
      detail: {
        resource: data,
        sourceId: 'gift-manager',
        data: {
          source: 'gift-manager',
          sections: data.sections,
          itemCount: Number.isFinite(itemCount) ? itemCount : 0,
        },
      },
    }));
  }

  #afterMutation() {
    this.#recheckQueued = true;
    this.#pendingCart = null;
  }

  #syncRuntimeRail(giftUnavailable, offerSubtotal, config) {
    if (giftUnavailable) {
      this.#setRuntimeUnavailable();
      return;
    }

    const rail = document.querySelector('[data-gift-preview]');
    if (!rail) return;

    // Defense in depth: gift-bracelet-config.liquid clamps giftThreshold to
    // at least 1 cent before it ever reaches this JSON (a merchant setting
    // it to 0 must never divide-by-zero here), but guard again in case the
    // config element is ever missing/malformed/stale (e.g. a cached
    // section render from before a threshold change).
    const threshold =
      Number.isFinite(config.giftThreshold) && config.giftThreshold > 0 ? config.giftThreshold : 1;

    let state = 'progress';
    if (offerSubtotal >= threshold) state = 'unlocked';

    this.#applyRailState(rail, state);
    // Head-start fill past the free-shipping marker (owner, 2026-09-19;
    // straightened 2026-09-28 so $115 no longer looks finished) — the same
    // curve as gift-bracelet-preview.liquid so a section re-render and a JS
    // update never disagree.
    let pct = Math.min((offerSubtotal * 100) / threshold, 100);
    const fsT = Number.isFinite(config.freeShippingThreshold) && config.freeShippingThreshold > 0 ? config.freeShippingThreshold : 0;
    if (fsT > 0 && fsT < threshold && offerSubtotal >= fsT && offerSubtotal < threshold) {
      const marker = Math.min(96, Math.max(4, (fsT * 100) / threshold));
      const t = Math.min(1, Math.max(0, (offerSubtotal - fsT) / (threshold - fsT)));
      const eased = 0.1 + 0.9 * t;
      pct = Math.min(100, Math.round(marker + (100 - marker) * eased));
    }
    rail.style.setProperty('--gift-progress', `${pct}%`);
    rail.style.setProperty('--gift-progress-n', `${pct}`);
    // Free-shipping checkpoint (owner, 2026-09-19): mirror the Liquid
    // is-reached state so the check mark and win line never wait on a morph.
    const fsReached = fsT > 0 && offerSubtotal >= fsT;
    rail
      .querySelectorAll('.gift-rail__mark--shipping, .gift-rail__label--shipping, [data-gift-win]')
      .forEach((el) => el.classList.toggle('is-reached', fsReached));
  }

  #setRuntimeUnavailable() {
    const rail = document.querySelector('[data-gift-preview]');
    if (!rail) return;

    this.#applyRailState(rail, 'soldout');
  }

  #applyRailState(rail, state) {
    PREVIEW_STATES.forEach((candidate) => rail.classList.remove(`gift-rail--${candidate}`));
    rail.classList.add(`gift-rail--${state}`);
    rail.dataset.giftStage = state;
  }

  #renderPreview() {
    const rail = document.querySelector('[data-gift-preview]');
    if (!rail) return;

    const state = this.#previewState || rail.dataset.giftStage || 'progress';
    this.#setRailState(state);

    const shouldShowGift = state === 'unlocked';
    this.#syncPreviewGiftLine(shouldShowGift);
    // Owner decision (2026-08-16): the drawer receipt keeps showing the
    // synthetic "FREE Gift" line (perceived value) — this staging-only demo
    // mirrors that same presentation so the theme-editor preview matches
    // what a real qualifying cart shows. Sourced from settings.gift_value_text
    // (via config.giftValueText), never a hardcoded amount.
    this.#syncPreviewReceipt(shouldShowGift);
  }

  #setRailState(state) {
    const rail = document.querySelector('[data-gift-preview]');
    const config = this.#config;
    if (!rail || !PREVIEW_STATES.has(state)) return;

    this.#applyRailState(rail, state);

    const progressByState = {
      progress: 72,
      unlocked: 100,
      soldout: 0,
    };
    rail.style.setProperty('--gift-progress', `${progressByState[state]}%`);
    rail.style.setProperty('--gift-progress-n', `${progressByState[state]}`);

    const message = rail.querySelector(`[data-gift-message='${state}']`);
    const previewMessage = config?.previewMessages?.[state];
    if (message && previewMessage) message.innerHTML = previewMessage;
  }

  #syncPreviewGiftLine(shouldShow) {
    const drawer = document.querySelector('.cart-drawer__dialog');
    if (!drawer) return;

    drawer.querySelectorAll('.kc-gift-preview-row').forEach((row) => row.remove());
    if (!shouldShow || drawer.querySelector('[data-kc-gift-line]:not(.kc-gift-preview-row)')) return;

    const tableBody = drawer.querySelector('.cart-drawer__items .cart-items__table tbody');
    const template = document.querySelector('[data-kc-gift-preview-row]');
    if (!tableBody || !template) return;

    tableBody.append(template.content.cloneNode(true));
  }

  #syncPreviewReceipt(shouldShow) {
    const drawer = document.querySelector('.cart-drawer__dialog');
    if (!drawer) return;

    const subtotal = drawer.querySelector('.cart-totals__drawer-subtotal-value');
    const discounts = drawer.querySelector('.cart-totals__discounts-value');
    const details = drawer.querySelector('.cart-totals__discounts-details');
    const adjustedValues = [subtotal, discounts].filter(Boolean);

    drawer.querySelectorAll('[data-kc-gift-preview-discount]').forEach((line) => line.remove());

    adjustedValues.forEach((element) => {
      if (element.dataset.kcPreviewOriginalText === undefined) return;
      element.textContent = element.dataset.kcPreviewOriginalText;
      element.dataset.kcAmountCents = element.dataset.kcPreviewOriginalCents;
      delete element.dataset.kcPreviewOriginalText;
      delete element.dataset.kcPreviewOriginalCents;
    });

    if (!shouldShow || !subtotal || !discounts || !details) return;

    // Sourced from settings.gift_value_text (config.giftValueText), not a
    // hardcoded amount. Robust parse: strip everything but digits/decimal
    // point; an unparsable/blank setting yields 0, which omits the
    // synthetic line entirely rather than injecting "$0.00" or NaN.
    const giftValueCents = this.#parseGiftValueCents(this.#config?.giftValueText);
    if (giftValueCents <= 0) return;

    [subtotal, discounts].forEach((element) => {
      const originalText = element.textContent.trim();
      const originalCents = Number.parseInt(element.dataset.kcAmountCents || '', 10);
      if (!Number.isFinite(originalCents)) return;

      element.dataset.kcPreviewOriginalText = originalText;
      element.dataset.kcPreviewOriginalCents = String(originalCents);
      element.dataset.kcAmountCents = String(originalCents + giftValueCents);
      element.textContent = this.#formatPreviewMoney(originalText, originalCents + giftValueCents);
    });

    const giftValueDisplay = (giftValueCents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const line = document.createElement('span');
    line.className = 'cart-totals__discount-line';
    line.dataset.kcGiftPreviewDiscount = 'true';
    line.innerHTML =
      '<span class="cart-totals__discount-title">FREE Gift</span>' +
      `<span class="cart-totals__discount-amount cart-secondary-typography">&minus; $${giftValueDisplay}</span>`;
    details.append(line);
  }

  #parseGiftValueCents(text) {
    if (typeof text !== 'string') return 0;
    const numeric = Number.parseFloat(text.replace(/[^0-9.]/g, ''));
    return Number.isFinite(numeric) ? Math.round(numeric * 100) : 0;
  }

  #formatPreviewMoney(originalText, cents) {
    const firstDigit = originalText.search(/\d/);
    const lastDigit = Math.max(...[...originalText].map((character, index) => (/\d/.test(character) ? index : -1)));
    const amount = (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    if (firstDigit === -1 || lastDigit === -1) return `$${amount}`;
    return `${originalText.slice(0, firstDigit)}${amount}${originalText.slice(lastDigit + 1)}`;
  }
}

/**
 * Estimated Total pulse (motion pass, 2026-09-19): on every cart update the
 * drawer footer's total value dips opacity then returns via the
 * .is-updating class (styled in snippets/cart-summary.liquid), so a price
 * change reads as "just updated" rather than a silent swap. Re-queries the
 * element on each event rather than caching it, since morphSection can
 * replace the node the update event is reporting on.
 */
document.addEventListener(CART_UPDATE_EVENT, () => {
  const totalValue = document.querySelector('.cart-totals--footer .cart-totals__total-value');
  if (!totalValue) return;

  totalValue.classList.add('is-updating');
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      totalValue.classList.remove('is-updating');
    });
  });
});

function initializeGiftManager() {
  if (!window.__kiianGiftManager) {
    window.__kiianGiftManager = new GiftManager();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeGiftManager, { once: true });
} else {
  initializeGiftManager();
}
